/**
 * SharePoint document downloads via the Microsoft Graph API.
 *
 * Three entry points share one request/response pipeline: a PDF-converting
 * download, the same download with progress reporting, and a raw download used
 * when Graph cannot convert the file.
 */

import { acquireDelegatedGraphToken } from '../utils/msalTokenService';

export interface SharePointDocument {
  sharepointSiteId?: string;
  sharepointDriveId?: string;
  sharepointItemId?: string;
  originalFilename?: string;
}

export interface ExtendedError extends Error {
  code?: string;
}

/** Progress callback for tracking download progress */
export type DownloadProgressCallback = (progress: {
  loaded: number;
  total: number | null;
  percentage: number | null;
}) => void;

interface GraphItem {
  siteId: string;
  driveId: string;
  itemId: string;
}

const codedError = (message: string, code: string): ExtendedError => {
  const error: ExtendedError = new Error(message);
  error.code = code;
  return error;
};

/**
 * Check if a document has SharePoint identifiers
 */
export const hasSharePointIdentifiers = (userDocument: SharePointDocument): boolean => {
  return !!(
    userDocument?.sharepointSiteId &&
    userDocument?.sharepointDriveId &&
    userDocument?.sharepointItemId
  );
};

const requireGraphItem = (userDocument: SharePointDocument): GraphItem => {
  const { sharepointSiteId, sharepointDriveId, sharepointItemId } = userDocument;

  if (!sharepointSiteId || !sharepointDriveId || !sharepointItemId) {
    console.error('❌ [SharePointDownload] Missing SharePoint identifiers:', {
      hasSiteId: !!sharepointSiteId,
      hasDriveId: !!sharepointDriveId,
      hasItemId: !!sharepointItemId,
    });
    throw codedError(
      'Missing SharePoint identifiers - please refresh the project context',
      'MISSING_IDENTIFIERS'
    );
  }

  return { siteId: sharepointSiteId, driveId: sharepointDriveId, itemId: sharepointItemId };
};

const isPdfFile = (originalFilename?: string): boolean =>
  !!originalFilename && originalFilename.toLowerCase().endsWith('.pdf');

/**
 * Fetch item content from Graph. PDFs are fetched natively (Graph returns 406
 * for ?format=pdf on a PDF); Office formats are converted server-side.
 *
 * The raw (non-converting) caller omits the Accept header entirely, matching
 * its pre-unification request.
 */
const fetchGraphContent = async (
  userDocument: SharePointDocument,
  convertToPdf: boolean,
  { includeAcceptHeader = true }: { includeAcceptHeader?: boolean } = {}
): Promise<Response> => {
  const { siteId, driveId, itemId } = requireGraphItem(userDocument);
  const graphToken: string = await acquireDelegatedGraphToken();
  const format = convertToPdf ? '?format=pdf' : '';

  const headers: Record<string, string> = {
    Authorization: `Bearer ${graphToken}`,
  };
  if (includeAcceptHeader) {
    headers.Accept = 'application/pdf, application/octet-stream';
  }

  return fetch(
    `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${itemId}/content${format}`,
    {
      method: 'GET',
      headers,
    }
  );
};

/**
 * Options for the generic (non-coded) Graph failure below. Each entry point
 * kept a distinct shape for this message before the pipeline was unified:
 * the progress-tracking download included the response body, the raw
 * download used "download" rather than "fetch" and didn't log through the
 * progress path's silent branch.
 */
interface GenericFailureOptions {
  verb?: 'fetch' | 'download';
  includeBodyInError?: boolean;
  logFailure?: boolean;
  logLabel?: string;
}

/**
 * Translate a Graph failure into the structured errors the preview UI handles:
 * revoked access, a file Graph cannot convert, or an unexpected status.
 */
const assertGraphResponseOk = async (
  response: Response,
  userDocument: SharePointDocument,
  convertedToPdf: boolean,
  {
    verb = 'fetch',
    includeBodyInError = false,
    logFailure = true,
    logLabel = 'Graph API request failed',
  }: GenericFailureOptions = {}
): Promise<void> => {
  if (response.status === 401 || response.status === 403) {
    console.error('❌ [SharePointDownload] SharePoint access denied (403/401)');
    throw codedError(
      'SharePoint access denied - your permissions may have been revoked',
      'SHAREPOINT_DENIED'
    );
  }

  // Graph returns 400/406 when it cannot convert the file to PDF
  if (convertedToPdf && (response.status === 400 || response.status === 406)) {
    const errorText: string = await response.text();
    console.warn(
      '⚠️ [SharePointDownload] Graph PDF conversion not supported for this file:',
      userDocument.originalFilename,
      response.status,
      errorText
    );
    throw codedError(
      'This document contains elements which cannot be converted for browser preview',
      'CONVERSION_NOT_SUPPORTED'
    );
  }

  if (!response.ok) {
    const errorText: string = await response.text();
    if (logFailure) {
      console.error(`❌ [SharePointDownload] ${logLabel}:`, response.status, errorText);
    }
    const bodySuffix = includeBodyInError ? ` - ${errorText}` : '';
    throw new Error(
      `Failed to ${verb} from SharePoint: ${response.status} ${response.statusText}${bodySuffix}`
    );
  }
};

/** Structured errors pass through; anything else is wrapped for the caller. */
const rethrowDownloadError = (
  error: unknown,
  logLabel: string = 'SharePoint download failed'
): never => {
  if ((error as ExtendedError).code) {
    throw error;
  }
  console.error(`❌ [SharePointDownload] ${logLabel}:`, error);
  throw new Error(`SharePoint download error: ${(error as Error).message}`);
};

/** Stream the body so the caller can report how much has arrived. */
const readWithProgress = async (
  response: Response & { body: ReadableStream<Uint8Array> },
  onProgress: DownloadProgressCallback
): Promise<Blob> => {
  const contentLength = response.headers.get('Content-Length');
  const total = contentLength ? parseInt(contentLength, 10) : null;

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  let chunk = await reader.read();
  while (!chunk.done) {
    chunks.push(chunk.value);
    loaded += chunk.value.length;
    onProgress({
      loaded,
      total,
      percentage: total ? Math.round((loaded / total) * 100) : null,
    });
    chunk = await reader.read();
  }

  const combined = new Uint8Array(chunks.reduce((acc, part) => acc + part.length, 0));
  let offset = 0;
  for (const part of chunks) {
    combined.set(part, offset);
    offset += part.length;
  }
  return new Blob([combined], { type: 'application/pdf' });
};

/**
 * Download document content from SharePoint, converting Office formats to PDF.
 */
export const downloadFromSharePoint = async (userDocument: SharePointDocument): Promise<Blob> => {
  const convertToPdf = !isPdfFile(userDocument.originalFilename);

  try {
    const response = await fetchGraphContent(userDocument, convertToPdf);
    await assertGraphResponseOk(response, userDocument, convertToPdf);
    return await response.blob();
  } catch (error) {
    return rethrowDownloadError(error);
  }
};

/**
 * Download document content from SharePoint with progress tracking.
 */
export const downloadFromSharePointWithProgress = async (
  userDocument: SharePointDocument,
  onProgress?: DownloadProgressCallback
): Promise<Blob> => {
  const convertToPdf = !isPdfFile(userDocument.originalFilename);

  try {
    const response = await fetchGraphContent(userDocument, convertToPdf);
    await assertGraphResponseOk(response, userDocument, convertToPdf, {
      includeBodyInError: true,
      logFailure: false,
    });

    // Without a body or a listener there is nothing to report against
    if (!response.body || !onProgress) {
      return await response.blob();
    }

    return await readWithProgress(response, onProgress);
  } catch (error) {
    return rethrowDownloadError(error, 'SharePoint download with progress failed');
  }
};

/**
 * Download raw file content from SharePoint without PDF conversion.
 * Used when Graph's ?format=pdf conversion fails.
 */
export const downloadRawFromSharePoint = async (
  userDocument: SharePointDocument
): Promise<Blob> => {
  const response = await fetchGraphContent(userDocument, false, { includeAcceptHeader: false });
  await assertGraphResponseOk(response, userDocument, false, {
    verb: 'download',
    logLabel: 'Graph raw download failed',
  });
  return response.blob();
};
